/**
 * 时间展示格式化（纯函数，两端共用，任务 8d）。
 *
 * 会话列表时间口径对齐旧版 ImTimeFormatter.conversationTime（im_time_formatter.dart）：
 * 今天→HH:mm、昨天（日历日差 1）→「昨天」、同年→M/d、跨年→yyyy/M/d。
 * 与旧版一致的边界：未来日期（本地时钟偏差）落到 M/d 分支，同日未来时间仍是 HH:mm。
 *
 * 气泡时间/日期分隔条（任务 9d）对齐旧版 messageBubbleTime/messageDateSeparator。
 */

/** 本地时区日历日序数（每个日期用自己的 offset，跨 DST 也按日历日比较） */
function dayOrdinal(d: Date): number {
  return Math.floor((d.getTime() - d.getTimezoneOffset() * 60_000) / 86_400_000);
}

/**
 * 本地日历日序数（messageModel 日期分隔条的分组键/稳定 key 用）。
 * ≤0 的时间戳返回 0——调用方对无时间的行不产生分隔条。
 */
export function localDayOrdinal(timestampMs: number): number {
  if (timestampMs <= 0) return 0;
  return dayOrdinal(new Date(timestampMs));
}

/** 语音时长：m:ss（旧版 _formatDuration 同口径，秒向下取整）。≤0 返回 0:00。 */
export function formatVoiceDuration(ms: number): string {
  const seconds = ms > 0 ? Math.floor(ms / 1000) : 0;
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

/** 气泡时间：HH:mm（旧版 messageBubbleTime）。≤0 返回空串。 */
export function formatBubbleTime(timestampMs: number): string {
  if (timestampMs <= 0) return '';
  const d = new Date(timestampMs);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/**
 * 日期分隔条：今天/昨天/M月d日（同年）/yyyy年M月d日（旧版 messageDateSeparator）。
 * 「今天/昨天」随 now 变化——UI 层按 nowMs 重算（useNowMinute 口径），不要缓存文案。
 */
export function formatDateSeparator(timestampMs: number, nowMs: number): string {
  if (timestampMs <= 0) return '';
  const d = new Date(timestampMs);
  const now = new Date(nowMs);
  const diffDays = dayOrdinal(now) - dayOrdinal(d);
  if (diffDays === 0) return '今天';
  if (diffDays === 1) return '昨天';
  if (d.getFullYear() === now.getFullYear()) return `${d.getMonth() + 1}月${d.getDate()}日`;
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
}

/** @param timestampMs 毫秒时间戳（≤0 返回空串——占位行无消息时间） */
export function formatConversationTime(timestampMs: number, nowMs: number): string {
  if (timestampMs <= 0) return '';
  const d = new Date(timestampMs);
  const now = new Date(nowMs);
  const diffDays = dayOrdinal(now) - dayOrdinal(d);
  if (diffDays === 0) {
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }
  if (diffDays === 1) return '昨天';
  if (d.getFullYear() === now.getFullYear()) return `${d.getMonth() + 1}/${d.getDate()}`;
  return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}`;
}

/**
 * 最近上线时间：今天→HH:mm、昨天→「昨天 HH:mm」、更早→「M月d日 HH:mm」
 * （旧版 friendOfflineTime 同口径）。≤0 返回空串。
 *
 * **一处刻意修旧版**：跨年补年份（旧版恒 M月d日，2024 年的最后上线会显示成
 * 「3月5日 10:00」分不清年份；applyTime 分支本就带年，这里取同一口径）。
 */
export function formatLastSeenTime(timestampMs: number, nowMs: number): string {
  if (timestampMs <= 0) return '';
  const d = new Date(timestampMs);
  const now = new Date(nowMs);
  const hm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  const diffDays = dayOrdinal(now) - dayOrdinal(d);
  if (diffDays <= 0) return hm;
  if (diffDays === 1) return `昨天 ${hm}`;
  if (d.getFullYear() === now.getFullYear()) return `${d.getMonth() + 1}月${d.getDate()}日 ${hm}`;
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日 ${hm}`;
}
